import { PlusIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { updateGeneralSettings, updateMonthlyCounter, updateCounter } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { useAllGeneralSettings } from '../all-settings-context';
import { VatRateComboboxField, vatRateToComboboxItem } from '../../order/vatRate/VatRateComboboxField';
import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { captureException } from '@sentry/react';
import { Input } from '../../../components/input/Input';
import useSWR from 'swr';
import { fetchFeatureFlags } from '../../feature-flag/models';
import { Card } from 'components/Card';

const MONTH_NAMES = [
  'Januari',
  'Februari',
  'Maart',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Augustus',
  'September',
  'Oktober',
  'November',
  'December',
];

function formatYearMonth(yearMonth: string): string {
  const yy = parseInt(yearMonth.slice(0, 2), 10);
  const mm = parseInt(yearMonth.slice(2, 4), 10);
  const year = 2000 + yy;
  const monthName = MONTH_NAMES[mm - 1] ?? `Maand ${mm}`;
  return `${monthName} ${year}`;
}

function getCurrentYearMonth(): string {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `${yy}${mm}`;
}

interface MonthlyCounterRowProps {
  yearMonth: string;
  nextValue: number;
  counterType: string;
  onSaved: () => void;
}

const MonthlyCounterRow: React.FC<MonthlyCounterRowProps> = ({ yearMonth, nextValue, counterType, onSaved }) => {
  const [value, setValue] = useState(String(nextValue));
  const [isSaving, setIsSaving] = useState(false);

  const hasChanged = value !== String(nextValue);

  const handleSave = async () => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1) {
      toast.error('Waarde moet minstens 1 zijn');
      return;
    }
    setIsSaving(true);
    try {
      await updateMonthlyCounter({
        counterType,
        yearMonth,
        nextValue: numValue,
      });
      toast.success(`Counter ${formatYearMonth(yearMonth)} aangepast`);
      onSaved();
    } catch (err) {
      captureException(err);
      toast.error('Kon counter niet aanpassen: ' + getDisplayError(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-48 text-sm font-medium text-fg">{formatYearMonth(yearMonth)}</div>
      <div className="w-16 text-xs text-fg-muted">{yearMonth}</div>
      <div className="w-32">
        <Input type="number" value={value} onChange={setValue} />
      </div>
      {hasChanged && (
        <Button
          type="button"
          color="primary"
          onTrigger={handleSave}
          isDisabled={isSaving}
          isLoading={isSaving}
          iconLeft={<PlusIcon className="button-icon" />}
        >
          Opslaan
        </Button>
      )}
    </div>
  );
};

interface InvoiceCounterSectionProps {
  nextValue: number;
  onSaved: () => void;
}

const InvoiceCounterSection: React.FC<InvoiceCounterSectionProps> = ({ nextValue, onSaved }) => {
  const [value, setValue] = useState(String(nextValue));
  const [isSaving, setIsSaving] = useState(false);

  const hasChanged = value !== String(nextValue);

  const handleSave = async () => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1) {
      toast.error('Waarde moet minstens 1 zijn');
      return;
    }
    setIsSaving(true);
    try {
      await updateCounter({
        counterType: 'INVOICE',
        nextValue: numValue,
      });
      toast.success('Factuur counter aangepast');
      onSaved();
    } catch (err) {
      captureException(err);
      toast.error('Kon counter niet aanpassen: ' + getDisplayError(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-48 text-sm font-medium text-fg">Volgend nummer</div>
      <div className="w-32">
        <Input type="number" value={value} onChange={setValue} />
      </div>
      {hasChanged && (
        <Button
          type="button"
          color="primary"
          onTrigger={handleSave}
          isDisabled={isSaving}
          isLoading={isSaving}
          iconLeft={<PlusIcon className="button-icon" />}
        >
          Opslaan
        </Button>
      )}
    </div>
  );
};

interface CreditNoteCounterSectionProps {
  nextValue: number;
  onSaved: () => void;
}

const CreditNoteCounterSection: React.FC<CreditNoteCounterSectionProps> = ({ nextValue, onSaved }) => {
  const [value, setValue] = useState(String(nextValue));
  const [isSaving, setIsSaving] = useState(false);

  const hasChanged = value !== String(nextValue);

  const handleSave = async () => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1) {
      toast.error('Waarde moet minstens 1 zijn');
      return;
    }
    setIsSaving(true);
    try {
      await updateCounter({
        counterType: 'CREDIT_NOTE',
        nextValue: numValue,
      });
      toast.success('Credit nota counter aangepast');
      onSaved();
    } catch (err) {
      captureException(err);
      toast.error('Kon counter niet aanpassen: ' + getDisplayError(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card title="Verkoopdagboek - Credit nota's" subtitle="Credit nota's gebruiken één doorlopende nummering.">
      <div className="flex items-center gap-4">
        <div className="w-48 text-sm font-medium text-fg">Volgend nummer</div>
        <div className="w-32">
          <Input type="number" value={value} onChange={setValue} />
        </div>
        {hasChanged && (
          <Button
            type="button"
            color="primary"
            onTrigger={handleSave}
            isDisabled={isSaving}
            isLoading={isSaving}
            iconLeft={<PlusIcon className="button-icon" />}
          >
            Opslaan
          </Button>
        )}
      </div>
    </Card>
  );
};

const vatSettingsSchema = Yup.object().shape({});

interface IVatSettingsValues {
  reverseChargedVatRate: ISimpleComboboxItem | null;
}

const InvoiceSettingsPage = () => {
  const { settings, refreshData } = useAllGeneralSettings();
  const [newYearMonth, setNewYearMonth] = useState('');

  const { data: featureFlags } = useSWR('feature-flags', fetchFeatureFlags);
  const useNewInvoiceNumbers = featureFlags?.find((f) => f.id === 'USE_NEW_INVOICE_NUMBERS')?.enabled ?? false;

  const invoiceCounters = settings.monthlyInvoiceCounters ?? [];
  const invoiceCounter = settings.invoiceNumber ?? 1;
  const creditNoteCounter = settings.creditNoteCounter ?? 1;

  const initialValues: IVatSettingsValues = useMemo(() => {
    return {
      reverseChargedVatRate: settings.reverseChargedVatRate
        ? vatRateToComboboxItem(settings.reverseChargedVatRate)
        : null,
    };
  }, [settings]);

  const handleAddMonth = async (counterType: string) => {
    const ym = newYearMonth || getCurrentYearMonth();
    if (!/^\d{4}$/.test(ym)) {
      toast.error('Ongeldig formaat, gebruik JJMM (bv. 2603)');
      return;
    }
    try {
      await updateMonthlyCounter({
        counterType,
        yearMonth: ym,
        nextValue: 1,
      });
      toast.success(`Maand ${formatYearMonth(ym)} toegevoegd`);
      setNewYearMonth('');
      refreshData();
    } catch (err) {
      captureException(err);
      toast.error('Kon maand niet toevoegen: ' + getDisplayError(err));
    }
  };

  return (
    <div className="grid gap-4">
      <Card
        title="Verkoopdagboek - Facturen"
        subtitle={
          useNewInvoiceNumbers
            ? 'Elke maand heeft een eigen nummering en dagboek (VK01-VK12). Factuurnummers worden opgebouwd als JJMM + volgnummer (bv. 260400001).'
            : 'Facturen gebruiken één doorlopende nummering.'
        }
      >
        {useNewInvoiceNumbers ? (
          <div className="flex flex-col gap-2">
            {invoiceCounters.length === 0 && (
              <p className="text-sm text-fg-muted">
                Nog geen maandelijkse counters. Voeg een maand toe of maak een eerste factuur aan.
              </p>
            )}

            {invoiceCounters.map((counter) => (
              <MonthlyCounterRow
                key={counter.yearMonth}
                yearMonth={counter.yearMonth}
                nextValue={counter.nextValue}
                counterType={counter.counterType}
                onSaved={refreshData}
              />
            ))}

            <div className="mt-2 flex items-center gap-4">
              <div className="w-32">
                <Input
                  type="text"
                  value={newYearMonth}
                  onChange={setNewYearMonth}
                  placeholder={getCurrentYearMonth()}
                />
              </div>
              <Button
                type="button"
                color="secondary"
                onTrigger={() => handleAddMonth('INVOICE')}
                iconLeft={<PlusIcon className="button-icon" />}
              >
                Maand toevoegen
              </Button>
            </div>
          </div>
        ) : (
          <InvoiceCounterSection nextValue={invoiceCounter} onSaved={refreshData} />
        )}
      </Card>

      <CreditNoteCounterSection nextValue={creditNoteCounter} onSaved={refreshData} />

      <Formik
        initialValues={initialValues}
        validationSchema={vatSettingsSchema}
        onSubmit={async (values) => {
          try {
            const patch: Partial<typeof values> = diff(initialValues, values);
            const { reverseChargedVatRate } = patch;
            await updateGeneralSettings({
              reverseChargedVatRateId: reverseChargedVatRate ? reverseChargedVatRate?.key : undefined,
            });
            toast.success('Instellingen aangepast');
          } catch (err: any) {
            captureException(err);
            toast.error('Kon instellingen niet aanpassen: ' + getDisplayError(err));
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => {
          return (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <Card title="BTW instellingen">
                <VatRateComboboxField labelText="Btw type voor verlegde btw" name="reverseChargedVatRate" />
              </Card>

              <div>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  iconLeft={<PlusIcon className="button-icon" />}
                >
                  Pas instellingen aan
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default InvoiceSettingsPage;
